import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Link2, Trash2, Star } from 'lucide-react';
import { Section } from './ProposalEditor';
import { TemplateMenu, saveTemplate } from './TemplateMenu';

const inputCls = 'border border-stilo-ink/15 p-2 bg-stilo-bg text-sm focus:outline-none focus:border-stilo-ink';

const ItemRow = ({ it, onChange, onRemove }) => (
  <div className="flex gap-4 border-b border-stilo-ink/10 py-4" data-testid={`proposal-item-${it.id}`}>
    {it.image ? <img src={it.image} alt="" className="w-20 h-20 object-cover bg-stilo-beige" /> : <div className="w-20 h-20 bg-stilo-beige" />}
    <div className="flex-1 grid grid-cols-1 sm:grid-cols-6 gap-2">
      <input value={it.title} onChange={e => onChange({ title: e.target.value })} className={`${inputCls} sm:col-span-4`} data-testid="item-title" />
      <input type="number" step="0.01" min="0" value={it.price} onChange={e => onChange({ price: Math.max(0, Number(e.target.value) || 0) })} className={inputCls} aria-label="Precio" data-testid="item-price" />
      <input type="number" min="1" value={it.qty} onChange={e => onChange({ qty: Math.max(1, parseInt(e.target.value, 10) || 1) })} className={inputCls} aria-label="Cantidad" data-testid="item-qty" />
      <input value={it.note} onChange={e => onChange({ note: e.target.value })} placeholder="Nota (opcional): dónde va, por qué…" className={`${inputCls} sm:col-span-6`} data-testid="item-note" />
      <a href={it.url} target="_blank" rel="noreferrer" className="text-xs text-stilo-ink3 truncate sm:col-span-6 hover:text-stilo-gold">{it.url}</a>
    </div>
    <div className="self-start flex flex-col gap-3">
      <button onClick={() => saveTemplate('product', it.title, { url: it.url, title: it.title, image: it.image, price: it.price })}
        className="text-stilo-ink3 hover:text-stilo-gold" aria-label="Guardar en favoritos" title="Guardar en favoritos" data-testid="item-favorite"><Star className="w-4 h-4" /></button>
      <button onClick={onRemove} className="text-stilo-ink3 hover:text-stilo-terra" aria-label="Quitar" data-testid="item-remove"><Trash2 className="w-4 h-4" /></button>
    </div>
  </div>
);

export const ProductsEditor = ({ items, setItems, total }) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const add = async () => {
    if (!url.trim()) return;
    setLoading(true);
    try {
      const r = await api.post('/product-preview', { url });
      setItems([...items, { id: crypto.randomUUID(), qty: 1, note: '', ...r.data, title: r.data.title || 'Producto' }]);
      setUrl('');
      if (!r.data.price) toast.message('No encontré el precio: escríbelo a mano');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pude leer ese enlace');
    } finally { setLoading(false); }
  };

  const update = (id, patch) => setItems(items.map(i => (i.id === id ? { ...i, ...patch } : i)));

  return (
    <Section title="Shopping list" testid="proposal-products-section"
      actions={<TemplateMenu kind="product" label="Favoritos" testid="tpl-product" applyLabel="Añadir"
        onApply={(d) => setItems([...items, { ...d, id: crypto.randomUUID(), qty: 1, note: '' }])} />}>
      <div className="flex gap-2">
        <input value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()}
          placeholder="Pega el enlace del producto de Stilo Decor" className={`${inputCls} flex-1 p-3`} data-testid="product-url-input" />
        <button onClick={add} disabled={loading} className="btn-stilo disabled:opacity-50" data-testid="product-add">
          <Link2 className="w-4 h-4" strokeWidth={1.5} /> {loading ? 'Leyendo…' : 'Añadir'}
        </button>
      </div>
      {items.length === 0 && <p className="text-sm text-stilo-ink3 mt-4">Todavía no hay productos en la lista.</p>}
      <div className="mt-2">
        {items.map(it => <ItemRow key={it.id} it={it} onChange={(p) => update(it.id, p)} onRemove={() => setItems(items.filter(x => x.id !== it.id))} />)}
      </div>
      {items.length > 0 && <p className="text-right mt-4 font-serif text-2xl" data-testid="proposal-total">Total: {total}</p>}
    </Section>
  );
};
